/**
 * İstemci hata bildiricisi: kullanıcının yaşadığı sorunu backend'e yollar.
 *
 * Yakalar: başarısız API istekleri (fetchMonitor), JS hataları ve
 * yakalanmamış promise'ler, yüklenemeyen sayfa dosyaları (deploy sonrası eski
 * chunk), sinirli tıklama (lastAction). Ekran çökmeleri AppErrorBoundary'den
 * reportClientError ile gelir. Backend kaydeder ve SADECE PROD'da ekibe mail
 * atar (backend services/client_error_service).
 *
 * Gürültü kontrolü: aynı sorun 60 sn'de bir kez, sayfa başına dakikada en fazla
 * 20 rapor, oturum yoksa hiç (uç token istiyor). Bildirim hiçbir koşulda
 * uygulamayı bozmaz.
 */

import { config } from '../../utils/config';
import { wrapFetch } from './fetchMonitor';
import { getLastAction, installClickTracking, resetLastAction } from './lastAction';
import type { ClientErrorReport, ReportInput } from './types';

const REPORT_PATH = '/v2/client-errors/';
const DEDUPE_MS = 60_000;
const MAX_PER_MINUTE = 20;
const IGNORED = [/ResizeObserver loop/i, /^Script error\.?$/i];
/** Ağ hatasını fetchMonitor zaten 'network' olarak bildirdi. */
const ALREADY_REPORTED = /Failed to fetch|NetworkError when attempting to fetch|Load failed/i;
const CHUNK_ERROR =
  /Loading (CSS )?chunk [\w-]+ failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i;

const recent = new Map<string, number>();
let sentAt: number[] = [];
let originalFetch: typeof fetch | null = null;
let removeClickTracking: (() => void) | null = null;

export const isChunkError = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'ChunkLoadError' || CHUNK_ERROR.test(error.message));

function originOf(url: string): string {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return '';
  }
}

/** Kendi backend'lerimiz (RFQ API ve portal API); başka siteler değil. */
export function isOwnApi(url: string): boolean {
  const target = originOf(url);
  if (!target) return false;
  if ([config.API_BASE_URL, config.PORTAL_API_URL].map(originOf).includes(target)) return true;
  return target === window.location.origin && new URL(url, window.location.origin).pathname.startsWith('/api');
}

export const isReportUrl = (url: string): boolean => url.includes('/client-errors');

function allowed(input: ReportInput, now: number): boolean {
  const key = [input.kind, input.method, input.status, (input.endpoint || '').split('?')[0], (input.message || '').slice(0, 80)].join('|');
  const previous = recent.get(key);
  if (previous !== undefined && now - previous < DEDUPE_MS) return false;
  sentAt = sentAt.filter((t) => now - t < 60_000);
  if (sentAt.length >= MAX_PER_MINUTE) return false;
  recent.set(key, now);
  sentAt.push(now);
  return true;
}

export function reportClientError(input: ReportInput, now = Date.now()): void {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token || !originalFetch || !allowed(input, now)) return;
    const report: ClientErrorReport = {
      ...input,
      page_url: window.location.href,
      route: window.location.pathname,
      user_agent: navigator.userAgent,
      last_action: getLastAction(now),
    };
    void originalFetch(`${config.API_BASE_URL.replace(/\/$/, '')}${REPORT_PATH}`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(report),
    }).catch(() => undefined);
  } catch {
    /* bildirim asla uygulamayı bozmaz */
  }
}

function onWindowError(event: ErrorEvent): void {
  const message = event.message || (event.error instanceof Error ? event.error.message : '');
  if (!message || IGNORED.some((re) => re.test(message))) return;
  if (/^(chrome|moz|safari(-web)?)-extension:/.test(event.filename || '')) return;
  const file = (event.filename || '').split('/').pop();
  const where = file ? ` @ ${file}:${event.lineno}` : '';
  reportClientError({ kind: isChunkError(event.error) || CHUNK_ERROR.test(message) ? 'chunk' : 'js', message: `${message}${where}` });
}

function onRejection(event: PromiseRejectionEvent): void {
  const reason: unknown = event.reason;
  const name = reason instanceof Error ? reason.name : '';
  // ApiError: fetchMonitor aynı isteği zaten bildirdi. AbortError: kasıtlı iptal.
  if (name === 'AbortError' || name === 'ApiError') return;
  const message = reason instanceof Error ? reason.message : String(reason ?? '');
  if (!message || IGNORED.some((re) => re.test(message)) || ALREADY_REPORTED.test(message)) return;
  reportClientError({ kind: isChunkError(reason) ? 'chunk' : 'js', message });
}

/** Uygulama açılışında bir kez çağrılır (src/index.tsx). */
export function installErrorReporter(): void {
  if (originalFetch || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  originalFetch = window.fetch.bind(window);
  window.fetch = wrapFetch(originalFetch, { isOwnApi, isReportUrl, report: (input) => reportClientError(input) });
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onRejection);
  removeClickTracking = installClickTracking((label) =>
    reportClientError({ kind: 'rage_click', message: `Aynı öğeye art arda tıklandı: ${label}` }),
  );
}

/** Testler ve hot reload için her şeyi geri alır. */
export function uninstallErrorReporter(): void {
  if (originalFetch) window.fetch = originalFetch;
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onRejection);
  removeClickTracking?.();
  originalFetch = null;
  removeClickTracking = null;
  recent.clear();
  sentAt = [];
  resetLastAction();
}
